import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Grid,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { LocationTrailMap } from '@/components/pmv/map';
import pmvService from '@/services/pmv.service';
import type { PmvConnectedProvider, PmvTelemetrySignalRow } from '@/services/pmv.service';
import { connectivityLabel, connectivityTagColor, formatLastSeen, freshnessBadge } from '@/utils/pmvConnectedCopy';
import { useAuthStore } from '@/store/authStore';
import { usePmvEquipmentLabels } from '@/hooks/usePmvEquipmentLabels';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;
const { RangePicker } = DatePicker;

/** Equipment link with human label (never raw UUID as primary text). */
const PmvUnitLink: React.FC<{ id: string }> = ({ id }) => {
  const { resolve } = usePmvEquipmentLabels();
  return <Link to={`/asset/pmv/${id}`}>{resolve(id, `${id.slice(0, 8)}…`)}</Link>;
};

type LocHistoryRange = 'today' | '24h' | '7d' | 'custom';

function locHistoryBounds(
  range: LocHistoryRange,
  custom: [Dayjs, Dayjs] | null
): { from?: string; to?: string } {
  const now = dayjs();
  if (range === 'today') {
    return { from: now.startOf('day').toISOString(), to: now.toISOString() };
  }
  if (range === '24h') {
    return { from: now.subtract(24, 'hour').toISOString(), to: now.toISOString() };
  }
  if (range === '7d') {
    return { from: now.subtract(7, 'day').toISOString(), to: now.toISOString() };
  }
  if (custom?.[0] && custom?.[1]) {
    return { from: custom[0].startOf('day').toISOString(), to: custom[1].endOf('day').toISOString() };
  }
  return {};
}

function dwellLabel(stationarySince: string | null | undefined): string | null {
  if (!stationarySince) return null;
  const ms = Date.now() - new Date(stationarySince).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

function spatialEventLabel(eventType: string): string {
  return eventType.replace(/_/g, ' ');
}

type OnboardRow = {
  external_device_id: string;
  display_name?: string;
  serial_number?: string | null;
  device_type?: string | null;
  already_registered?: boolean;
  match_confidence?: string;
  suggested_match?: string | null;
  pmv_profile_id?: string | null;
  selected?: boolean;
};

type ContradictionRow = { pmv_profile_id: string; type: string; detail: string };

export const PmvConnectedOverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const summaryQ = useQuery({
    queryKey: ['pmv-connected-summary'],
    queryFn: () => pmvService.getConnectedSummary(),
    refetchInterval: 60_000,
  });
  const s = summaryQ.data;

  const tiles = [
    { label: 'Connected units', value: s?.connected_units ?? '—', path: '/asset/pmv/connected/devices' },
    { label: 'Live', value: s?.online ?? '—', path: '/asset/pmv/connected' },
    { label: 'Delayed', value: s?.delayed ?? '—', path: '/asset/pmv/connected' },
    { label: 'Offline', value: s?.offline ?? '—', path: '/asset/pmv/connected' },
    { label: 'Unmapped devices', value: s?.unmapped_devices ?? '—', path: '/asset/pmv/connected/devices?unassigned=1' },
    {
      label: 'Open reconciliation',
      value: s?.open_reconciliation ?? '—',
      path: '/asset/pmv/connected/reconciliation',
    },
    { label: 'Provider issues', value: s?.provider_issues ?? '—', path: '/asset/pmv/connected/providers' },
  ];

  const spatialTiles = [
    {
      label: 'Location reporting',
      value: s?.spatial?.with_location ?? '—',
      path: '/asset/pmv/connected/map',
    },
    {
      label: 'Stale location',
      value: s?.spatial?.stale_location ?? '—',
      path: '/asset/pmv/connected/map',
    },
    {
      label: 'No location',
      value: s?.spatial?.no_location ?? '—',
      path: '/asset/pmv/connected/map',
    },
    {
      label: 'Location mismatches',
      value: s?.spatial?.location_mismatches ?? '—',
      path: '/asset/pmv/connected/movement-attention?type=LOCATION_CONTEXT_MISMATCH',
    },
    {
      label: 'Restricted-area',
      value: s?.spatial?.restricted_area ?? '—',
      path: '/asset/pmv/connected/movement-attention?type=RESTRICTED_AREA',
    },
    {
      label: 'Unexpected movement',
      value: s?.spatial?.unexpected_movement ?? '—',
      path: '/asset/pmv/connected/movement-attention?type=UNEXPECTED_MOVEMENT',
    },
  ];

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Connected Fleet"
          documentNumber=""
          statusTag={<Tag color="blue">Wave 2 spatial</Tag>}
          subtitle={
            <Text type="secondary">
              Connectivity is a second dimension beside PMV operational status — telemetry never silently changes
              deployment or certified meters.
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button type="primary" onClick={() => navigate('/asset/pmv/connected/map')}>
                Live Fleet Map
              </Button>
              <Button onClick={() => navigate('/asset/pmv/connected/movement-attention')}>
                Spatial attention
              </Button>
              <Button onClick={() => navigate('/asset/pmv/connected/geofences')}>Geofences</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/devices')}>Devices</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/providers')}>Providers</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/reconciliation')}>Reconciliation</Button>
              <Button onClick={() => navigate('/asset/pmv/connected/diagnostics')}>Diagnostics</Button>
              <Button onClick={() => navigate('/asset/pmv')}>Fleet Register</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {summaryQ.isError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load Connected Fleet summary"
            description={(summaryQ.error as Error)?.message || 'Check permissions (pmv.connected.view) and kill switch.'}
          />
        ) : null}
        <Row gutter={[12, 12]}>
          {tiles.map((t) => (
            <Col xs={12} sm={8} md={4} key={t.label}>
              <Card size="small" hoverable onClick={() => navigate(t.path)} loading={summaryQ.isLoading}>
                <Text type="secondary" style={{ fontSize: screens.xs ? 12 : undefined }}>
                  {t.label}
                </Text>
                <Title level={screens.xs ? 4 : 3} style={{ margin: 0 }}>
                  {t.value}
                </Title>
              </Card>
            </Col>
          ))}
        </Row>
        <Card title="Spatial (location evidence)" size="small" loading={summaryQ.isLoading}>
          <Row gutter={[12, 12]}>
            {spatialTiles.map((t) => (
              <Col xs={12} sm={8} md={4} key={t.label}>
                <Card size="small" hoverable onClick={() => navigate(t.path)}>
                  <Text type="secondary">{t.label}</Text>
                  <Title level={4} style={{ margin: 0 }}>
                    {t.value}
                  </Title>
                </Card>
              </Col>
            ))}
          </Row>
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Location never silently changes PMV status or allocation. Drill into map or spatial attention.
          </Text>
        </Card>
        <Card title="Attention" size="small" loading={summaryQ.isLoading}>
          {(s?.attention ?? []).length === 0 ? (
            <Text type="secondary">No connected-fleet attention items.</Text>
          ) : (
            <Space direction="vertical" style={{ width: '100%' }}>
              {s!.attention.map((a) => (
                <Alert
                  key={a.key}
                  type="warning"
                  showIcon
                  message={a.label}
                  action={
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(
                          a.key === 'reconciliation' || a.key === 'meter_mismatch'
                            ? '/asset/pmv/connected/reconciliation'
                            : a.key === 'quarantine' || a.key === 'provider'
                              ? '/asset/pmv/connected/diagnostics'
                              : a.key === 'unmapped'
                                ? '/asset/pmv/connected/devices?unassigned=1'
                                : a.key === 'location_mismatch' ||
                                    a.key === 'restricted_area' ||
                                    a.key === 'unexpected_movement' ||
                                    a.key === 'stale_location'
                                  ? '/asset/pmv/connected/movement-attention'
                                  : '/asset/pmv/connected/map'
                        )
                      }
                    >
                      Open
                    </Button>
                  }
                />
              ))}
            </Space>
          )}
        </Card>
      </Space>
    </WorkspaceLayout>
  );
};

export const PmvConnectedDevicesPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [unassignedOnly, setUnassignedOnly] = useState(
    () => new URLSearchParams(window.location.search).get('unassigned') === '1'
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [assignOpen, setAssignOpen] = useState<{ id: string; label: string } | null>(null);
  const [histOpen, setHistOpen] = useState<{ id: string; label: string } | null>(null);
  const [regOpen, setRegOpen] = useState(false);
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [onboardProviderId, setOnboardProviderId] = useState<string | undefined>();
  const [onboardRows, setOnboardRows] = useState<OnboardRow[]>([]);
  const [csvText, setCsvText] = useState('');
  const [csvPreview, setCsvPreview] = useState<{
    valid: number;
    invalid: number;
    rows: Array<{ status: string; external_device_id?: string; message?: string }>;
  } | null>(null);
  const [form] = Form.useForm();
  const [regForm] = Form.useForm();

  const devicesQ = useQuery({
    queryKey: ['pmv-connected-devices', unassignedOnly, page, pageSize, search],
    queryFn: () =>
      pmvService.listConnectedDevices({
        unassigned_only: unassignedOnly,
        page,
        pageSize,
        search: search || undefined,
      }),
  });
  const providersQ = useQuery({
    queryKey: ['pmv-connected-providers'],
    queryFn: () => pmvService.listConnectedProviders(),
  });
  const profilesQ = useQuery({
    queryKey: ['pmv-profiles-labels'],
    queryFn: () => pmvService.listProfiles({ pageSize: 200 }),
  });

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createConnectedDevice(body),
    onSuccess: () => {
      message.success('Device registered');
      setRegOpen(false);
      regForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['pmv-connected-devices'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const discoverMut = useMutation({
    mutationFn: (providerId: string) => pmvService.discoverConnectedDevices(providerId),
    onSuccess: (r) => {
      setOnboardRows(
        (r.devices || []).map((d) => ({
          ...d,
          selected: !d.already_registered && d.match_confidence !== 'AMBIGUOUS',
          pmv_profile_id: d.match_confidence === 'EXACT' || d.match_confidence === 'LIKELY' ? d.suggested_match : null,
        }))
      );
      message.success(
        `Discovered ${r.counts?.discovered ?? r.devices?.length ?? 0} — exact ${r.counts?.exact ?? 0}, review ${r.counts?.ambiguous ?? 0}`
      );
    },
    onError: (e: Error) => message.error(e.message),
  });

  const bulkMut = useMutation({
    mutationFn: () => {
      if (!onboardProviderId) throw new Error('Select provider');
      const rows = onboardRows
        .filter((r) => r.selected && !r.already_registered)
        .map((r) => ({
          external_device_id: r.external_device_id,
          serial_number: r.serial_number,
          device_type: r.device_type,
          pmv_profile_id: r.pmv_profile_id || null,
          register_only: !r.pmv_profile_id,
        }));
      if (!rows.length) throw new Error('Select at least one new device');
      return pmvService.bulkOnboardConnectedDevices(onboardProviderId, { mode: 'partial', rows });
    },
    onSuccess: (r) => {
      message.success(`Onboarded ${r.ok}/${r.total} (${r.error} errors)`);
      setOnboardOpen(false);
      setCsvText('');
      setCsvPreview(null);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-devices'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const csvPreviewMut = useMutation({
    mutationFn: () => {
      if (!onboardProviderId) throw new Error('Select provider');
      return pmvService.previewBulkOnboardCsv(onboardProviderId, csvText);
    },
    onSuccess: (r) => {
      setCsvPreview({ valid: r.valid, invalid: r.invalid, rows: r.rows });
      const validRows = (r.rows || []).filter((row) => row.status === 'valid' && row.external_device_id);
      if (validRows.length) {
        setOnboardRows((prev) => {
          const byId = new Map(prev.map((p) => [p.external_device_id, p]));
          for (const row of validRows) {
            const ext = row.external_device_id!;
            if (!byId.has(ext)) {
              byId.set(ext, {
                external_device_id: ext,
                selected: true,
                match_confidence: 'NO_MATCH',
                pmv_profile_id: null,
              });
            }
          }
          return Array.from(byId.values());
        });
      }
      message.success(`CSV preview: ${r.valid} valid, ${r.invalid} invalid`);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const assignMut = useMutation({
    mutationFn: (v: { deviceId: string; pmv_profile_id: string }) =>
      pmvService.assignConnectedDevice(v.deviceId, { pmv_profile_id: v.pmv_profile_id }),
    onSuccess: () => {
      message.success('Device assigned (history preserved)');
      setAssignOpen(null);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-devices'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const simMut = useMutation({
    mutationFn: (external_device_id: string) =>
      pmvService.simulateConnectedIngest({
        provider_key: 'MOCK',
        external_device_id,
        location: { lat: 24.4539, lon: 54.3773, accuracy_m: 10 },
        ignition: true,
        engine_hours: 100 + Math.random() * 10,
        speed_kmh: 0,
      }),
    onSuccess: (r) => {
      message.success(`Simulated ingest accepted=${r.accepted} dup=${r.duplicates}`);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
      void qc.invalidateQueries({ queryKey: ['pmv-equipment-connectivity'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-diagnostics'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const histQ = useQuery({
    queryKey: ['pmv-device-assignments', histOpen?.id],
    queryFn: () => pmvService.listConnectedDeviceAssignments(histOpen!.id),
    enabled: Boolean(histOpen?.id),
  });

  const providerName = useMemo(() => {
    const m = new Map((providersQ.data ?? []).map((p) => [p.id, p.display_name || p.provider_key]));
    return (id: string) => m.get(id) || id;
  }, [providersQ.data]);

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Connected devices"
          documentNumber=""
          statusTag={<Tag>Identity</Tag>}
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Overview</Button>
              <Button
                onClick={() => {
                  setOnboardOpen(true);
                  setOnboardRows([]);
                  setOnboardProviderId(providersQ.data?.[0]?.id);
                }}
              >
                Bulk onboard
              </Button>
              <Button type="primary" onClick={() => setRegOpen(true)}>
                Register device
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space wrap>
          <Select
            value={unassignedOnly ? 'unassigned' : 'all'}
            style={{ width: 220 }}
            onChange={(v) => {
              setUnassignedOnly(v === 'unassigned');
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All devices' },
              { value: 'unassigned', label: 'Unmapped only' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="Search external ID / serial"
            style={{ width: 260 }}
            onSearch={(v) => {
              setSearch(v);
              setPage(1);
            }}
          />
        </Space>
        <Table
          rowKey="id"
          loading={devicesQ.isLoading}
          dataSource={devicesQ.data?.items ?? []}
          scroll={{ x: 720 }}
          pagination={{
            current: page,
            pageSize,
            total: devicesQ.data?.total ?? 0,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          locale={{
            emptyText: devicesQ.isError
              ? (devicesQ.error as Error)?.message || 'Failed to load devices'
              : 'No devices — register a tracker and assign it to a PMV unit',
          }}
          columns={[
            { title: 'External ID', dataIndex: 'external_device_id' },
            {
              title: 'Provider',
              dataIndex: 'provider_id',
              render: (id: string) => providerName(id),
            },
            { title: 'Type', dataIndex: 'device_type' },
            { title: 'Status', dataIndex: 'status' },
            {
              title: 'Last seen',
              dataIndex: 'last_seen_at',
              render: (v: string | null) => formatLastSeen(v),
            },
            {
              title: 'Actions',
              render: (_: unknown, r: { id: string; external_device_id: string }) => (
                <Space size={4} wrap>
                  <Button size="small" onClick={() => setAssignOpen({ id: r.id, label: r.external_device_id })}>
                    Assign
                  </Button>
                  <Button
                    size="small"
                    loading={simMut.isPending}
                    onClick={() => simMut.mutate(r.external_device_id)}
                  >
                    Feed sample
                  </Button>
                  <Button size="small" onClick={() => setHistOpen({ id: r.id, label: r.external_device_id })}>
                    History
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title="Register device"
        open={regOpen}
        onCancel={() => setRegOpen(false)}
        onOk={() => regForm.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form
          form={regForm}
          layout="vertical"
          initialValues={{ device_type: 'GPS_TRACKER' }}
          onFinish={(v) => createMut.mutate(v)}
        >
          <Form.Item name="provider_id" label="Provider" rules={[{ required: true }]}>
            <Select
              options={(providersQ.data ?? []).map((p) => ({
                value: p.id,
                label: `${p.display_name} (${p.provider_key})`,
              }))}
            />
          </Form.Item>
          <Form.Item name="external_device_id" label="External device ID" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="device_type" label="Device type">
            <Select
              options={[
                { value: 'GPS_TRACKER', label: 'GPS tracker' },
                { value: 'ENGINE_GATEWAY', label: 'Engine gateway' },
                { value: 'FUEL_SENSOR', label: 'Fuel sensor' },
                { value: 'OTHER', label: 'Other' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Assign ${assignOpen?.label ?? ''}`}
        open={Boolean(assignOpen)}
        onCancel={() => setAssignOpen(null)}
        onOk={() => form.submit()}
        confirmLoading={assignMut.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => assignOpen && assignMut.mutate({ deviceId: assignOpen.id, ...v })}
        >
          <Form.Item name="pmv_profile_id" label="PMV unit" rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              options={(profilesQ.data?.data ?? []).map((p) => ({
                value: p.id,
                label: `${p.fleet_number || p.asset_code} — ${p.asset_name}`,
              }))}
            />
          </Form.Item>
          <Text type="secondary">Prior open assignment is closed; history is never overwritten.</Text>
        </Form>
      </Modal>

      <Modal
        title={`Assignment history — ${histOpen?.label ?? ''}`}
        open={Boolean(histOpen)}
        onCancel={() => setHistOpen(null)}
        footer={<Button onClick={() => setHistOpen(null)}>Close</Button>}
        width={720}
      >
        <Text type="secondary">
          Time-bound assignments are append-only. Historical telemetry resolves by observed_at into the correct window.
        </Text>
        <Table
          style={{ marginTop: 12 }}
          size="small"
          rowKey="id"
          loading={histQ.isLoading}
          dataSource={histQ.data ?? []}
          pagination={false}
          columns={[
            {
              title: 'PMV profile',
              dataIndex: 'pmv_profile_id',
              render: (id: string) => <PmvUnitLink id={id} />,
            },
            {
              title: 'From',
              dataIndex: 'effective_from',
              render: (v: string) => String(v).slice(0, 19).replace('T', ' '),
            },
            {
              title: 'To',
              dataIndex: 'effective_to',
              render: (v: string | null) => (v ? String(v).slice(0, 19).replace('T', ' ') : 'Open'),
            },
            { title: 'Reason', dataIndex: 'assignment_reason', ellipsis: true },
          ]}
        />
      </Modal>

      <Modal
        title="Bulk device onboard"
        open={onboardOpen}
        onCancel={() => setOnboardOpen(false)}
        width={960}
        okText="Confirm onboard"
        confirmLoading={bulkMut.isPending}
        onOk={() => bulkMut.mutate()}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Human confirmation required"
            description="Ambiguous matches are never auto-assigned. Exact/Likely suggestions can be edited before confirm. Mode: validated partial."
          />
          <Space wrap>
            <Select
              placeholder="Provider"
              style={{ width: 280 }}
              value={onboardProviderId}
              onChange={setOnboardProviderId}
              options={(providersQ.data ?? []).map((p) => ({
                value: p.id,
                label: `${p.display_name} (${p.provider_key})`,
              }))}
            />
            <Button
              loading={discoverMut.isPending}
              disabled={!onboardProviderId}
              onClick={() => onboardProviderId && discoverMut.mutate(onboardProviderId)}
            >
              Discover
            </Button>
          </Space>
          <Input.TextArea
            rows={3}
            placeholder="Optional CSV: external_device_id,pmv_profile_id,register_only"
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
          />
          <Button
            loading={csvPreviewMut.isPending}
            disabled={!onboardProviderId || !csvText.trim()}
            onClick={() => csvPreviewMut.mutate()}
          >
            Preview CSV
          </Button>
          {csvPreview ? (
            <Text type="secondary">
              Preview valid {csvPreview.valid} / invalid {csvPreview.invalid} — review table below before confirm
            </Text>
          ) : null}
          <Table<OnboardRow>
            size="small"
            rowKey="external_device_id"
            dataSource={onboardRows}
            pagination={false}
            scroll={{ y: 360 }}
            columns={[
              {
                title: 'Sel',
                width: 50,
                render: (_value, r, idx) => (
                  <input
                    type="checkbox"
                    checked={Boolean(r.selected)}
                    disabled={r.already_registered}
                    onChange={(e) => {
                      const next = [...onboardRows];
                      next[idx] = { ...next[idx], selected: e.target.checked };
                      setOnboardRows(next);
                    }}
                  />
                ),
              },
              { title: 'External ID', dataIndex: 'external_device_id' },
              { title: 'Name', dataIndex: 'display_name', ellipsis: true },
              {
                title: 'Match',
                dataIndex: 'match_confidence',
                render: (v: string | undefined, r) =>
                  r.already_registered ? <Tag>Registered</Tag> : <Tag>{v}</Tag>,
              },
              {
                title: 'PMV unit',
                render: (_value, r, idx) => (
                  <Select
                    allowClear
                    size="small"
                    style={{ width: 220 }}
                    disabled={r.already_registered || r.match_confidence === 'AMBIGUOUS'}
                    value={r.pmv_profile_id || undefined}
                    options={(profilesQ.data?.data ?? []).map((p) => ({
                      value: p.id,
                      label: `${p.fleet_number || p.asset_code}`,
                    }))}
                    onChange={(v) => {
                      const next = [...onboardRows];
                      next[idx] = { ...next[idx], pmv_profile_id: v || null };
                      setOnboardRows(next);
                    }}
                  />
                ),
              },
            ]}
          />
        </Space>
      </Modal>
    </WorkspaceLayout>
  );
};

export const PmvConnectedProvidersPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState<{
    id: string;
    display_name: string;
    sync_mode?: string;
    poll_interval_sec?: number | null;
    has_webhook_secret?: boolean;
    online_threshold_sec?: number;
    delayed_threshold_sec?: number;
  } | null>(null);
  const [runsOpen, setRunsOpen] = useState<{ id: string; label: string } | null>(null);
  const [tokenOnce, setTokenOnce] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['pmv-connected-providers'],
    queryFn: () => pmvService.listConnectedProviders(),
  });

  const runsQ = useQuery({
    queryKey: ['pmv-connected-sync-runs', runsOpen?.id],
    queryFn: () => pmvService.listConnectedProviderSyncRuns(runsOpen!.id),
    enabled: Boolean(runsOpen?.id),
  });

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => pmvService.createConnectedProvider(body),
    onSuccess: (data) => {
      message.success('Provider created');
      setOpen(false);
      form.resetFields();
      if (data?.ingest_token) setTokenOnce(data.ingest_token);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) =>
      pmvService.patchConnectedProvider(v.id, v.body),
    onSuccess: () => {
      message.success('Provider updated');
      setEditOpen(null);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => pmvService.patchConnectedProvider(id, { rotate_ingest_token: true }),
    onSuccess: (data) => {
      if (data?.ingest_token) setTokenOnce(data.ingest_token);
      message.success('Ingest token rotated — copy it now');
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const confirmRotate = (id: string) => {
    Modal.confirm({
      title: 'Rotate ingest token?',
      content: 'The previous token stops working immediately. Copy the new token before closing the banner.',
      okText: 'Rotate',
      okType: 'danger',
      onOk: () => rotateMut.mutateAsync(id),
    });
  };

  const testMut = useMutation({
    mutationFn: (id: string) => pmvService.testConnectedProvider(id),
    onSuccess: (r) => {
      message.success(r.message || (r.ok ? 'Connection OK' : 'Connection failed'));
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const syncMut = useMutation({
    mutationFn: (id: string) => pmvService.syncConnectedProviderNow(id),
    onSuccess: (r) => {
      message.success(`Sync ${r.status}: accepted ${r.events_accepted}, dup ${r.duplicates}`);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const pauseMut = useMutation({
    mutationFn: (id: string) => pmvService.pauseConnectedProvider(id),
    onSuccess: () => {
      message.success('Provider paused');
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const resumeMut = useMutation({
    mutationFn: (id: string) => pmvService.resumeConnectedProvider(id),
    onSuccess: () => {
      message.success('Provider resumed');
      void qc.invalidateQueries({ queryKey: ['pmv-connected-providers'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const discMut = useMutation({
    mutationFn: (id: string) => pmvService.discoverConnectedDevices(id),
    onSuccess: (r) => {
      Modal.info({
        title: 'Discovered devices',
        width: 720,
        content: (
          <Space direction="vertical">
            <Text type="secondary">
              exact {r.counts?.exact ?? 0} · likely {r.counts?.likely ?? 0} · ambiguous{' '}
              {r.counts?.ambiguous ?? 0} · unmatched {r.counts?.unmatched ?? 0}
            </Text>
            <ul>
              {(r.devices || []).map((d) => (
                <li key={d.external_device_id}>
                  {d.display_name || d.external_device_id}
                  {d.already_registered ? ' (registered)' : ` — ${d.match_confidence || 'NEW'}`}
                  {d.suggested_label ? ` → ${d.suggested_label}` : ''}
                </li>
              ))}
            </ul>
            <Button type="link" onClick={() => navigate('/asset/pmv/connected/devices')}>
              Open Devices → Bulk onboard
            </Button>
          </Space>
        ),
      });
    },
    onError: (e: Error) => message.error(e.message),
  });

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Connected providers"
          documentNumber=""
          statusTag={<Tag>Adapters</Tag>}
          primaryActions={
            <Space>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Overview</Button>
              <Button type="primary" onClick={() => setOpen(true)}>
                Add provider
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {listQ.isError ? (
          <Alert
            type="error"
            showIcon
            message="Could not load providers"
            description={(listQ.error as Error)?.message || 'Check pmv.connected.provider.view permission.'}
          />
        ) : null}
        {tokenOnce ? (
          <Alert
            type="warning"
            showIcon
            closable
            onClose={() => setTokenOnce(null)}
            message="Copy ingest token now (shown once)"
            description={
              <Text copyable code>
                {tokenOnce}
              </Text>
            }
          />
        ) : null}
        <Table<PmvConnectedProvider>
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          scroll={{ x: 960 }}
          locale={{ emptyText: 'No providers yet — add MOCK for Wave 0 simulation' }}
          columns={[
            { title: 'Key', dataIndex: 'provider_key' },
            { title: 'Name', dataIndex: 'display_name' },
            { title: 'Adapter', dataIndex: 'adapter_type' },
            {
              title: 'Sync',
              dataIndex: 'sync_mode',
              render: (m: string | undefined, r) => (
                <Space size={4}>
                  <Tag>{m || 'WEBHOOK'}</Tag>
                  {r.is_paused ? <Tag color="orange">Paused</Tag> : null}
                  {r.poll_interval_sec ? <Text type="secondary">{r.poll_interval_sec}s</Text> : null}
                </Space>
              ),
            },
            {
              title: 'Health',
              dataIndex: 'health_status',
              render: (h: string) => (
                <Tag color={h === 'HEALTHY' ? 'success' : h === 'DEGRADED' || h === 'FAILED' ? 'warning' : 'default'}>
                  {h}
                </Tag>
              ),
            },
            { title: 'Status', dataIndex: 'status' },
            {
              title: 'HMAC secret',
              dataIndex: 'has_webhook_secret',
              render: (v: boolean | undefined) => (v ? 'Configured' : '—'),
            },
            {
              title: 'Ingest token',
              dataIndex: 'has_ingest_token',
              render: (v: boolean | undefined) => (v ? 'Bound' : '—'),
            },
            {
              title: 'Actions',
              width: 420,
              render: (_value, r) => (
                <Space size={4} wrap>
                  <Button size="small" loading={testMut.isPending} onClick={() => testMut.mutate(r.id)}>
                    Test
                  </Button>
                  <Button size="small" loading={syncMut.isPending} onClick={() => syncMut.mutate(r.id)}>
                    Sync
                  </Button>
                  <Button size="small" loading={discMut.isPending} onClick={() => discMut.mutate(r.id)}>
                    Discover
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditOpen({
                        id: r.id,
                        display_name: r.display_name,
                        sync_mode: r.sync_mode,
                        poll_interval_sec: r.poll_interval_sec,
                        has_webhook_secret: r.has_webhook_secret,
                        online_threshold_sec: r.online_threshold_sec,
                        delayed_threshold_sec: r.delayed_threshold_sec,
                      });
                      editForm.setFieldsValue({
                        display_name: r.display_name,
                        sync_mode: r.sync_mode || 'WEBHOOK',
                        poll_interval_sec: r.poll_interval_sec ?? undefined,
                        online_threshold_sec: r.online_threshold_sec ?? 300,
                        delayed_threshold_sec: r.delayed_threshold_sec ?? 3600,
                        webhook_secret: undefined,
                        clear_webhook_secret: false,
                      });
                    }}
                  >
                    Credentials
                  </Button>
                  <Button size="small" onClick={() => setRunsOpen({ id: r.id, label: r.display_name })}>
                    Runs
                  </Button>
                  {r.is_paused ? (
                    <Button size="small" loading={resumeMut.isPending} onClick={() => resumeMut.mutate(r.id)}>
                      Resume
                    </Button>
                  ) : (
                    <Button size="small" loading={pauseMut.isPending} onClick={() => pauseMut.mutate(r.id)}>
                      Pause
                    </Button>
                  )}
                  <Button size="small" danger loading={rotateMut.isPending} onClick={() => confirmRotate(r.id)}>
                    Rotate token
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </Space>
      <Modal
        title="Add provider"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ provider_key: 'MOCK', adapter_type: 'MOCK', sync_mode: 'WEBHOOK' }}
          onFinish={(v) => createMut.mutate(v)}
        >
          <Form.Item name="provider_key" label="Provider key" rules={[{ required: true }]}>
            <Input placeholder="MOCK" />
          </Form.Item>
          <Form.Item name="display_name" label="Display name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="adapter_type" label="Adapter type">
            <Select options={[{ value: 'MOCK', label: 'MOCK / SIMULATED' }]} />
          </Form.Item>
          <Form.Item name="sync_mode" label="Sync mode">
            <Select
              options={[
                { value: 'WEBHOOK', label: 'WEBHOOK' },
                { value: 'POLL', label: 'POLL' },
                { value: 'HYBRID', label: 'HYBRID' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="poll_interval_sec"
            label="Poll interval (sec)"
            extra="Used when sync mode is POLL or HYBRID. Minimum 60."
          >
            <Input type="number" min={60} placeholder="300" />
          </Form.Item>
          <Form.Item name="online_threshold_sec" label="Live threshold (sec)" initialValue={300}>
            <Input type="number" min={30} />
          </Form.Item>
          <Form.Item name="delayed_threshold_sec" label="Delayed threshold (sec)" initialValue={3600}>
            <Input type="number" min={60} />
          </Form.Item>
          <Form.Item name="webhook_secret" label="Webhook HMAC secret (optional)">
            <Input.Password />
          </Form.Item>
          <Text type="secondary">
            An ingest token is generated automatically for webhook company binding (x-pmv-ingest-token).
          </Text>
        </Form>
      </Modal>
      <Modal
        title="Provider credentials & sync"
        open={Boolean(editOpen)}
        onCancel={() => setEditOpen(null)}
        onOk={() => editForm.submit()}
        confirmLoading={patchMut.isPending}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={(v) => {
            if (!editOpen) return;
            const body: Record<string, unknown> = {
              display_name: v.display_name,
              sync_mode: v.sync_mode,
            };
            if (v.poll_interval_sec != null && v.poll_interval_sec !== '') {
              body.poll_interval_sec = Number(v.poll_interval_sec);
            }
            if (v.online_threshold_sec != null && v.online_threshold_sec !== '') {
              body.online_threshold_sec = Number(v.online_threshold_sec);
            }
            if (v.delayed_threshold_sec != null && v.delayed_threshold_sec !== '') {
              body.delayed_threshold_sec = Number(v.delayed_threshold_sec);
            }
            if (v.clear_webhook_secret) body.clear_webhook_secret = true;
            else if (v.webhook_secret) body.webhook_secret = v.webhook_secret;
            patchMut.mutate({ id: editOpen.id, body });
          }}
        >
          <Form.Item name="display_name" label="Display name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sync_mode" label="Sync mode" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'WEBHOOK', label: 'WEBHOOK' },
                { value: 'POLL', label: 'POLL' },
                { value: 'HYBRID', label: 'HYBRID' },
              ]}
            />
          </Form.Item>
          <Form.Item name="poll_interval_sec" label="Poll interval (sec)">
            <Input type="number" min={60} placeholder="300" />
          </Form.Item>
          <Form.Item
            name="online_threshold_sec"
            label="Live threshold (sec)"
            extra="last_seen within this → Live"
          >
            <Input type="number" min={30} />
          </Form.Item>
          <Form.Item
            name="delayed_threshold_sec"
            label="Delayed threshold (sec)"
            extra="Between live and this → Delayed; older → Offline"
          >
            <Input type="number" min={60} />
          </Form.Item>
          <Form.Item
            name="webhook_secret"
            label="Set HMAC secret"
            extra={editOpen?.has_webhook_secret ? 'Leave blank to keep current secret.' : undefined}
          >
            <Input.Password placeholder="New secret" />
          </Form.Item>
          <Form.Item name="clear_webhook_secret" valuePropName="checked">
            <Checkbox>Clear HMAC secret</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={`Sync runs — ${runsOpen?.label || ''}`}
        open={Boolean(runsOpen)}
        onCancel={() => setRunsOpen(null)}
        footer={null}
        width={720}
      >
        <Table
          size="small"
          rowKey="id"
          loading={runsQ.isLoading}
          dataSource={runsQ.data ?? []}
          pagination={false}
          columns={[
            { title: 'Status', dataIndex: 'status', width: 110 },
            { title: 'Mode', dataIndex: 'mode', width: 90 },
            { title: 'Accepted', dataIndex: 'events_accepted', width: 90 },
            { title: 'Dup', dataIndex: 'duplicates', width: 70 },
            {
              title: 'Started',
              dataIndex: 'started_at',
              render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
            },
            { title: 'Error', dataIndex: 'error_message', ellipsis: true },
          ]}
        />
      </Modal>
    </WorkspaceLayout>
  );
};

export const PmvConnectedDiagnosticsPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const diagQ = useQuery({
    queryKey: ['pmv-connected-diagnostics'],
    queryFn: () => pmvService.getConnectedDiagnostics(),
    refetchInterval: 60_000,
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => pmvService.retryConnectedQuarantine(id),
    onSuccess: () => {
      message.success('Retry queued');
      void qc.invalidateQueries({ queryKey: ['pmv-connected-diagnostics'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const d = diagQ.data;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Connected diagnostics"
          documentNumber=""
          statusTag={<Tag color="orange">Data quality</Tag>}
          primaryActions={<Button onClick={() => navigate('/asset/pmv/connected')}>Overview</Button>}
        />
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={12}>
          <Col span={8}>
            <Card size="small">
              <Text type="secondary">Pending raw events</Text>
              <Title level={3}>{d?.pending_raw_events ?? '—'}</Title>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Text type="secondary">Unmapped devices</Text>
              <Title level={3}>{d?.unmapped_devices ?? '—'}</Title>
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Text type="secondary">Open quarantine</Text>
              <Title level={3}>{d?.open_quarantine?.length ?? '—'}</Title>
            </Card>
          </Col>
        </Row>

        <Card title="Provider health" size="small">
          <Table
            rowKey="id"
            pagination={false}
            dataSource={d?.providers ?? []}
            scroll={{ x: 480 }}
            columns={[
              { title: 'Provider', dataIndex: 'display_name' },
              { title: 'Key', dataIndex: 'provider_key' },
              {
                title: 'Health',
                dataIndex: 'health_status',
                render: (h: string) => <Tag>{h}</Tag>,
              },
              { title: 'Detail', dataIndex: 'health_detail', ellipsis: true },
            ]}
          />
        </Card>

        <Card title="Quarantine (unresolved telemetry — not attached to arbitrary units)" size="small">
          <Table
            rowKey="id"
            loading={diagQ.isLoading}
            dataSource={d?.open_quarantine ?? []}
            scroll={{ x: 640 }}
            columns={[
              { title: 'Reason', dataIndex: 'reason_code' },
              { title: 'External device', dataIndex: 'external_device_id' },
              { title: 'Detail', dataIndex: 'detail', ellipsis: true },
              {
                title: 'Created',
                dataIndex: 'created_at',
                render: (v: string) => new Date(v).toLocaleString(),
              },
              {
                title: 'Action',
                render: (_: unknown, r: { id: string }) => (
                  <Button size="small" loading={retryMut.isPending} onClick={() => retryMut.mutate(r.id)}>
                    Retry
                  </Button>
                ),
              },
            ]}
          />
        </Card>

        <Card title="Contradictions (advisory — no auto mutation)" size="small">
          <Table<ContradictionRow>
            rowKey={(r) => `${r.pmv_profile_id}-${r.type}-${r.detail}`}
            dataSource={(d?.contradictions ?? []) as ContradictionRow[]}
            scroll={{ x: 640 }}
            columns={[
              {
                title: 'Unit',
                dataIndex: 'pmv_profile_id',
                render: (id: string) => <PmvUnitLink id={id} />,
              },
              { title: 'Type', dataIndex: 'type', width: 160 },
              { title: 'Detail', dataIndex: 'detail' },
              {
                title: 'Action',
                width: 140,
                render: (_value, r) => (
                  <Button type="link" size="small" onClick={() => navigate(`/asset/pmv/${r.pmv_profile_id}`)}>
                    Open Passport
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </WorkspaceLayout>
  );
};

/** Passport Connected panel — Wave 2.9 spatial location / trail / timeline */
export const PmvPassportConnectedPanel: React.FC<{ profileId: string }> = ({ profileId }) => {
  const [signalCode, setSignalCode] = useState<string | undefined>('ENGINE_HOURS');
  const [locRange, setLocRange] = useState<LocHistoryRange>('24h');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  const bounds = useMemo(() => locHistoryBounds(locRange, customRange), [locRange, customRange]);

  const stateQ = useQuery({
    queryKey: ['pmv-connected-state', profileId],
    queryFn: () => pmvService.getEquipmentConnectedState(profileId),
    enabled: Boolean(profileId),
    refetchInterval: 60_000,
  });
  const connected = Boolean(stateQ.data?.connected);

  const movementQ = useQuery({
    queryKey: ['pmv-connected-movement', profileId],
    queryFn: () => pmvService.getEquipmentMovement(profileId),
    enabled: Boolean(profileId) && connected,
    refetchInterval: 60_000,
  });
  const compareQ = useQuery({
    queryKey: ['pmv-connected-spatial-compare', profileId],
    queryFn: () => pmvService.compareEquipmentSpatial(profileId),
    enabled: Boolean(profileId) && connected,
    refetchInterval: 60_000,
  });
  const eventsQ = useQuery({
    queryKey: ['pmv-connected-spatial-events', profileId],
    queryFn: () => pmvService.listEquipmentSpatialEvents(profileId, { limit: 80 }),
    enabled: Boolean(profileId) && connected,
  });
  const attentionQ = useQuery({
    queryKey: ['pmv-connected-spatial-attention', profileId],
    queryFn: () => pmvService.listEquipmentSpatialAttention(profileId, { status: 'OPEN' }),
    enabled: Boolean(profileId) && connected,
  });
  const membershipQ = useQuery({
    queryKey: ['pmv-connected-geofence-membership', profileId],
    queryFn: () => pmvService.listEquipmentGeofenceMembership(profileId),
    enabled: Boolean(profileId) && connected,
  });
  const locQ = useQuery({
    queryKey: ['pmv-connected-locations', profileId, locRange, bounds.from, bounds.to],
    queryFn: () =>
      pmvService.listEquipmentLocationHistory(profileId, {
        limit: 200,
        from: bounds.from,
        to: bounds.to,
      }),
    enabled: Boolean(profileId) && connected && (locRange !== 'custom' || Boolean(bounds.from && bounds.to)),
  });
  const signalsQ = useQuery({
    queryKey: ['pmv-connected-signals', profileId, signalCode],
    queryFn: () =>
      pmvService.listEquipmentConnectedSignals(profileId, {
        signal_code: signalCode,
        limit: 40,
      }),
    enabled: Boolean(profileId) && connected,
  });

  const data = stateQ.data;
  const st = data?.state as
    | {
        last_seen_at?: string;
        lat?: number;
        lon?: number;
        location_accuracy_m?: number;
        location_observed_at?: string;
        ignition?: string;
        telematics_engine_hours?: number;
        provider_key?: string;
        provider_name?: string;
        overall_quality?: string;
      }
    | null
    | undefined;

  if (stateQ.isLoading) return <Card loading />;

  if (!data?.connected) {
    return (
      <Alert
        type="info"
        showIcon
        message="Not connected"
        description={
          <Space direction="vertical">
            <Text>No telematics projection for this unit. Operational status remains authoritative.</Text>
            <Link to="/asset/pmv/connected/devices">Register / assign a device</Link>
          </Space>
        }
      />
    );
  }

  const mc = data.meter_comparison;
  const freshness = freshnessBadge(data.connectivity, st?.last_seen_at);
  const locFresh = freshnessBadge(data.connectivity, st?.location_observed_at || st?.last_seen_at);
  const assignments = (data.assignments ?? []) as Array<{
    id: string;
    device_id: string;
    effective_from: string;
    effective_to?: string | null;
  }>;
  const movement = movementQ.data;
  const compare = compareQ.data;
  const dwell = dwellLabel(movement?.stationary_since);
  const trailPoints = (locQ.data?.points ?? [])
    .filter((p) => p.lat != null && p.lon != null)
    .map((p) => ({
      lat: Number(p.lat),
      lon: Number(p.lon),
      observed_at: p.observed_at,
      quality: p.signal_quality,
    }));
  const locMeta = locQ.data?.meta;
  const timelineEvents = [...(eventsQ.data ?? [])].sort(
    (a, b) => new Date(a.observed_at).getTime() - new Date(b.observed_at).getTime()
  );

  const expectedMatch =
    compare?.expected?.source === 'ALLOCATION' &&
    compare.expected.project_id &&
    compare.observed.inside_project_ids.includes(String(compare.expected.project_id));

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type="info"
        showIcon
        message={`${data.operational_status?.replace(/_/g, ' ')} · ${freshness.label}`}
        description="Operational status and connectivity are independent. Location is evidence only — never silently changes PMV status, allocation, or certified meters."
      />

      <Card
        size="small"
        title="Current location"
        extra={
          <Link to={`/asset/pmv/connected/map`}>
            <Button size="small" type="primary">
              Open on Fleet Map
            </Button>
          </Link>
        }
      >
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="Observed coordinates">
            {st?.lat != null && st?.lon != null ? (
              <>
                {Number(st.lat).toFixed(5)}, {Number(st.lon).toFixed(5)}
                <Tag color={locFresh.live ? 'success' : 'warning'} style={{ marginLeft: 8 }}>
                  {locFresh.live ? 'Live' : connectivityLabel(data.connectivity)}
                </Tag>
              </>
            ) : (
              '—'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            {st?.location_observed_at ? formatLastSeen(st.location_observed_at) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="GPS accuracy">
            {st?.location_accuracy_m != null ? `±${st.location_accuracy_m} m` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Location quality">{st?.overall_quality || '—'}</Descriptions.Item>
          <Descriptions.Item label="Observed geofence">
            {compare?.observed.primary_geofence_code || '—'}
            {compare?.observed.restricted ? <Tag color="error" style={{ marginLeft: 8 }}>Restricted</Tag> : null}
          </Descriptions.Item>
          <Descriptions.Item label="Movement">
            {movement?.movement_state || 'UNKNOWN'}
            {dwell && movement?.movement_state === 'STATIONARY' ? ` · ${dwell}` : ''}
            {movement?.last_speed_kmh != null ? ` · ${movement.last_speed_kmh} km/h` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Expected context">
            {compare?.expected.source === 'ALLOCATION' ? (
              <>
                Allocation
                {compare.expected.project_id ? ` · project ${compare.expected.project_id.slice(0, 8)}…` : ''}
                <Tag color={expectedMatch ? 'success' : 'warning'} style={{ marginLeft: 8 }}>
                  {expectedMatch ? 'Project matches' : 'Context mismatch / review'}
                </Tag>
              </>
            ) : (
              'No active allocation expectation'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Connectivity">
            <Tag color={connectivityTagColor(data.connectivity)}>{connectivityLabel(data.connectivity)}</Tag>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              {freshness.label}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <Card size="small" title="Observed context" loading={compareQ.isLoading}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Quality">{compare?.observed.quality || '—'}</Descriptions.Item>
              <Descriptions.Item label="Inside projects">
                {compare?.observed.inside_project_ids?.length
                  ? compare.observed.inside_project_ids.map((id) => id.slice(0, 8)).join(', ')
                  : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Primary fence">{compare?.observed.primary_geofence_code || '—'}</Descriptions.Item>
              <Descriptions.Item label="Stable membership">
                {(membershipQ.data ?? []).length
                  ? membershipQ.data!.map((m) => `${m.geofence_id.slice(0, 8)}…=${m.stable_membership}`).join('; ')
                  : '—'}
              </Descriptions.Item>
            </Descriptions>
            <Text type="secondary">Observed inside a geofence does not mean allocated.</Text>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Expected context" loading={compareQ.isLoading}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Source">{compare?.expected.source || 'NONE'}</Descriptions.Item>
              <Descriptions.Item label="Ops status">
                {compare?.expected.operational_status?.replace(/_/g, ' ') || data.operational_status?.replace(/_/g, ' ')}
              </Descriptions.Item>
              <Descriptions.Item label="Allocation">
                {compare?.expected.allocation_id ? compare.expected.allocation_id.slice(0, 8) + '…' : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Findings">
                {(compare?.findings ?? []).length
                  ? compare!.findings.map((f) => <Tag key={f.type}>{f.type.replace(/_/g, ' ')}</Tag>)
                  : 'None'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Movement state" loading={movementQ.isLoading}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="State">{movement?.movement_state || 'UNKNOWN'}</Descriptions.Item>
          <Descriptions.Item label="Last observed">
            {movement?.last_observed_at ? formatLastSeen(movement.last_observed_at) : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="GPS Observed Distance">
            {movement != null
              ? `${Number(movement.gps_observed_distance_m).toFixed(0)} m`
              : '—'}
            <Text type="secondary" style={{ marginLeft: 8 }}>
              not odometer
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Last jump">
            {movement?.last_jump_at ? formatLastSeen(movement.last_jump_at) : '—'}
            {movement?.last_implied_speed_kmh != null
              ? ` · implied ${movement.last_implied_speed_kmh} km/h`
              : ''}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        size="small"
        title="Location history"
        extra={
          <Space wrap size="small">
            <Select
              size="small"
              value={locRange}
              style={{ width: 100 }}
              onChange={(v: LocHistoryRange) => setLocRange(v)}
              options={[
                { value: 'today', label: 'Today' },
                { value: '24h', label: '24h' },
                { value: '7d', label: '7d' },
                { value: 'custom', label: 'Custom' },
              ]}
            />
            {locRange === 'custom' ? (
              <RangePicker
                size="small"
                value={customRange}
                onChange={(v) => setCustomRange(v as [Dayjs, Dayjs] | null)}
              />
            ) : null}
          </Space>
        }
      >
        {locMeta ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Showing ${locMeta.points_returned} of ${locMeta.points_original} points · ${locMeta.sampling_method}`}
            description={locMeta.note}
          />
        ) : null}
        {mapError ? (
          <Alert type="warning" showIcon message="Trail map unavailable" description={mapError} style={{ marginBottom: 12 }} />
        ) : null}
        {trailPoints.length >= 2 ? (
          <div style={{ marginBottom: 12 }}>
            <LocationTrailMap points={trailPoints} height={280} onMapError={setMapError} />
          </div>
        ) : (
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {locQ.isLoading ? 'Loading trail…' : 'Need at least two location points for a trail map.'}
          </Text>
        )}
        <Table
          size="small"
          rowKey="id"
          loading={locQ.isLoading}
          pagination={{ pageSize: 10, size: 'small' }}
          dataSource={locQ.data?.points ?? []}
          locale={{ emptyText: 'No location signals in range' }}
          columns={[
            {
              title: 'Observed',
              dataIndex: 'observed_at',
              render: (v: string) => formatLastSeen(v),
              width: 120,
            },
            {
              title: 'Coordinates',
              render: (_: unknown, r: { lat?: number | null; lon?: number | null }) =>
                r.lat != null && r.lon != null
                  ? `${Number(r.lat).toFixed(5)}, ${Number(r.lon).toFixed(5)}`
                  : '—',
            },
            {
              title: 'Accuracy',
              dataIndex: 'accuracy_m',
              width: 90,
              render: (v: number | null) => (v != null ? `±${v}m` : '—'),
            },
            {
              title: 'Quality',
              dataIndex: 'signal_quality',
              width: 90,
              render: (q: string) => <Tag>{q || '—'}</Tag>,
            },
          ]}
        />
      </Card>

      <Card size="small" title="Movement / geofence timeline" loading={eventsQ.isLoading}>
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Evidence only — events are not fabricated when missing.
        </Text>
        {timelineEvents.length === 0 ? (
          <Text type="secondary">No spatial events recorded yet.</Text>
        ) : (
          <Timeline
            items={timelineEvents.map((e) => ({
              children: (
                <span>
                  <Text strong>{formatLastSeen(e.observed_at)}</Text>
                  {' · '}
                  {spatialEventLabel(e.event_type)}
                  {e.geofence_id ? ` · fence ${e.geofence_id.slice(0, 8)}…` : ''}
                  {e.quality ? ` · ${e.quality}` : ''}
                  {e.status && e.status !== 'CONFIRMED' ? ` · ${e.status}` : ''}
                </span>
              ),
            }))}
          />
        )}
      </Card>

      <Card size="small" title="Geofence events">
        <Table
          size="small"
          rowKey="id"
          loading={eventsQ.isLoading}
          pagination={{ pageSize: 8, size: 'small' }}
          dataSource={(eventsQ.data ?? []).filter((e) =>
            e.event_type === 'GEOFENCE_ENTER' || e.event_type === 'GEOFENCE_EXIT'
          )}
          locale={{ emptyText: 'No geofence enter/exit events' }}
          columns={[
            {
              title: 'Time',
              dataIndex: 'observed_at',
              width: 120,
              render: (v: string) => formatLastSeen(v),
            },
            {
              title: 'Event',
              dataIndex: 'event_type',
              width: 140,
              render: (v: string) => <Tag>{spatialEventLabel(v)}</Tag>,
            },
            {
              title: 'Geofence',
              dataIndex: 'geofence_id',
              ellipsis: true,
              render: (v: string | null) => (v ? v.slice(0, 8) + '…' : '—'),
            },
            {
              title: 'Location',
              key: 'loc',
              render: (_: unknown, r: { lat?: number | null; lon?: number | null }) =>
                r.lat != null && r.lon != null
                  ? `${Number(r.lat).toFixed(5)}, ${Number(r.lon).toFixed(5)}`
                  : '—',
            },
            {
              title: 'Quality',
              dataIndex: 'quality',
              width: 90,
              render: (q: string | null) => <Tag>{q || '—'}</Tag>,
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 100,
            },
          ]}
        />
      </Card>

      <Card size="small" title="Spatial attention" loading={attentionQ.isLoading}>
        {(attentionQ.data ?? []).length === 0 ? (
          <Text type="secondary">No open spatial attention cases.</Text>
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={attentionQ.data ?? []}
            columns={[
              { title: 'Type', dataIndex: 'case_type', render: (v: string) => v.replace(/_/g, ' ') },
              {
                title: 'Severity',
                dataIndex: 'severity',
                width: 100,
                render: (v: string) => <Tag color={v === 'HIGH' ? 'error' : 'warning'}>{v}</Tag>,
              },
              { title: 'Detail', dataIndex: 'detail', ellipsis: true },
              {
                title: 'Action',
                dataIndex: 'recommended_action',
                ellipsis: true,
              },
            ]}
          />
        )}
        <div style={{ marginTop: 8 }}>
          <Link to="/asset/pmv/connected/reconciliation">Open reconciliation</Link>
        </div>
      </Card>

      <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label="PMV operational status">
          <Tag>{data.operational_status?.replace(/_/g, ' ')}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Provider">
          {st?.provider_name || st?.provider_key || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Ignition">{st?.ignition || '—'}</Descriptions.Item>
        <Descriptions.Item label="Signal quality">{st?.overall_quality || '—'}</Descriptions.Item>
      </Descriptions>

      <Card size="small" title="Meter comparison (advisory)">
        <Descriptions column={1} size="small">
          <Descriptions.Item label="PMV certified meter">
            {mc.certified_engine_hours != null ? `${mc.certified_engine_hours} h` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Telematics engine hours">
            {mc.telematics_engine_hours != null ? `${mc.telematics_engine_hours} h` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Difference">
            {mc.delta_hours != null ? `${mc.delta_hours} h` : '—'}
          </Descriptions.Item>
        </Descriptions>
        <Text type="secondary">{mc.note}</Text>
        <div style={{ marginTop: 8 }}>
          <Link to="/asset/pmv/connected/reconciliation">Open reconciliation</Link>
        </div>
      </Card>

      <Card
        size="small"
        title="Signal history"
        extra={
          <Select
            allowClear
            placeholder="Signal"
            style={{ width: 180 }}
            value={signalCode}
            onChange={(v) => setSignalCode(v)}
            options={[
              { value: 'ENGINE_HOURS', label: 'Engine hours' },
              { value: 'LOCATION', label: 'Location' },
              { value: 'IGNITION', label: 'Ignition' },
              { value: 'SPEED', label: 'Speed' },
              { value: 'FUEL_LEVEL', label: 'Fuel level' },
            ]}
          />
        }
      >
        <Table<PmvTelemetrySignalRow>
          size="small"
          rowKey="id"
          loading={signalsQ.isLoading}
          pagination={false}
          dataSource={(signalsQ.data ?? []) as PmvTelemetrySignalRow[]}
          locale={{ emptyText: 'No signals for this filter' }}
          columns={[
            {
              title: 'Observed',
              dataIndex: 'observed_at',
              render: (v: string) => formatLastSeen(v),
              width: 120,
            },
            { title: 'Code', dataIndex: 'signal_code', width: 120 },
            {
              title: 'Quality',
              dataIndex: 'signal_quality',
              width: 90,
              render: (q: string) => <Tag>{q}</Tag>,
            },
            {
              title: 'Value',
              key: 'val',
              render: (_value, r) => {
                if (r.lat != null && r.lon != null) return `${Number(r.lat).toFixed(5)}, ${Number(r.lon).toFixed(5)}`;
                if (r.value_number != null) return `${r.value_number}${r.unit ? ` ${r.unit}` : ''}`;
                return r.value_text || '—';
              },
            },
            {
              title: 'Source',
              key: 'src',
              ellipsis: true,
              render: (_value, r) => String(r.provenance_json?.provider_key ?? '—'),
            },
          ]}
        />
      </Card>

      <Card size="small" title="Devices on this unit">
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={data.devices ?? []}
          columns={[
            { title: 'External ID', dataIndex: 'external_device_id' },
            { title: 'Type', dataIndex: 'device_type' },
            {
              title: 'Last seen',
              dataIndex: 'last_seen_at',
              render: (v: string | null) => formatLastSeen(v),
            },
          ]}
          locale={{ emptyText: 'No devices linked via assignment history' }}
        />
        {assignments.length > 0 && (
          <>
            <Title level={5} style={{ marginTop: 16 }}>
              Assignment history
            </Title>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={assignments}
              columns={[
                { title: 'Device', dataIndex: 'device_id', ellipsis: true },
                {
                  title: 'From',
                  dataIndex: 'effective_from',
                  render: (v: string) => String(v).slice(0, 19).replace('T', ' '),
                },
                {
                  title: 'To',
                  dataIndex: 'effective_to',
                  render: (v: string | null) => (v ? String(v).slice(0, 19).replace('T', ' ') : 'Open'),
                },
              ]}
            />
          </>
        )}
      </Card>

      {(data.contradictions ?? []).length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Contradictions (not auto-reconciled)"
          description={
            <ul style={{ marginBottom: 0 }}>
              {data.contradictions.map((c) => (
                <li key={c.type + c.detail}>
                  <strong>{c.type}</strong>: {c.detail}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <Button type="link" href="/asset/pmv/connected/diagnostics">
        Open Connected diagnostics
      </Button>
      <Button type="link" href="/asset/pmv/connected/reconciliation">
        Open Reconciliation
      </Button>
    </Space>
  );
};

export const PmvConnectedReconciliationPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('pmv.connected.reconciliation.manage') ||
      user?.permissions?.includes('pmv.admin')
  );
  const [status, setStatus] = useState<string | undefined>('OPEN');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [dismissOpen, setDismissOpen] = useState<string | null>(null);
  const [dismissReason, setDismissReason] = useState('');
  const [dismissForm] = Form.useForm();

  const listQ = useQuery({
    queryKey: ['pmv-connected-recon', status],
    queryFn: () => pmvService.listReconciliationCases({ status }),
  });

  const detailQ = useQuery({
    queryKey: ['pmv-connected-recon-detail', detailId],
    queryFn: () => pmvService.getReconciliationCase(detailId!),
    enabled: Boolean(detailId),
  });

  const refreshMut = useMutation({
    mutationFn: () => pmvService.refreshReconciliationCases(),
    onSuccess: (r) => {
      message.success(`Scanned ${r.scanned}, created ${r.created}`);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-recon'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const dismissMut = useMutation({
    mutationFn: (v: { id: string; reason: string }) => pmvService.dismissReconciliationCase(v.id, v.reason),
    onSuccess: () => {
      message.success('Dismissed — evidence retained');
      setDismissOpen(null);
      setDismissReason('');
      dismissForm.resetFields();
      void qc.invalidateQueries({ queryKey: ['pmv-connected-recon'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const promoteMut = useMutation({
    mutationFn: (id: string) => pmvService.promoteReconciliationCase(id),
    onSuccess: (r) => {
      message.info(r.authoritative_action.note);
      void qc.invalidateQueries({ queryKey: ['pmv-connected-recon'] });
      void qc.invalidateQueries({ queryKey: ['pmv-connected-summary'] });
      navigate(r.authoritative_action.path);
    },
    onError: (e: Error) => message.error(e.message),
  });

  const confirmPromote = (id: string, caseType: string) => {
    Modal.confirm({
      title: caseType === 'METER' ? 'Open meter update workflow?' : 'Open equipment Passport?',
      content:
        'This marks the case promoted and hands off to existing PMV workflows. Certified meters and ops status are not overwritten here.',
      okText: caseType === 'METER' ? 'Initiate meter update' : 'Open equipment',
      onOk: () => promoteMut.mutateAsync(id),
    });
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Telemetry reconciliation"
          documentNumber=""
          statusTag={<Tag color="orange">Advisory</Tag>}
          subtitle={
            <Text type="secondary">
              Review discrepancies, then open existing PMV workflows. Telemetry never overwrites certified meters or
              operational status here.
            </Text>
          }
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/connected')}>Overview</Button>
              <Button type="primary" loading={refreshMut.isPending} onClick={() => refreshMut.mutate()}>
                Refresh from contradictions
              </Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Select
          value={status || 'ALL'}
          style={{ width: 200 }}
          onChange={(v) => setStatus(v === 'ALL' ? undefined : v)}
          options={[
            { value: 'OPEN', label: 'Open' },
            { value: 'REVIEWED', label: 'Reviewed' },
            { value: 'PROMOTED', label: 'Promoted' },
            { value: 'DISMISSED', label: 'Dismissed' },
            { value: 'SUPERSEDED', label: 'Superseded' },
            { value: 'ALL', label: 'All' },
          ]}
        />
        {!canManage ? (
          <Alert type="info" showIcon message="View only — promote/dismiss requires pmv.connected.reconciliation.manage" />
        ) : null}
        {listQ.isError ? (
          <Alert type="error" showIcon message={(listQ.error as Error)?.message || 'Failed to load cases'} />
        ) : null}
        <Table
          rowKey="id"
          loading={listQ.isLoading}
          dataSource={listQ.data ?? []}
          scroll={{ x: 800 }}
          locale={{ emptyText: 'No telemetry differences currently require review.' }}
          columns={[
            { title: 'Type', dataIndex: 'case_type', width: 140 },
            { title: 'Severity', dataIndex: 'severity', width: 110 },
            { title: 'Status', dataIndex: 'status', width: 110 },
            {
              title: 'Unit',
              dataIndex: 'pmv_profile_id',
              render: (id: string) => <PmvUnitLink id={id} />,
            },
            { title: 'Detail', dataIndex: 'detail', ellipsis: true },
            {
              title: 'Actions',
              width: 320,
              render: (_: unknown, r: { id: string; status: string; case_type: string }) => (
                <Space wrap>
                  <Button size="small" onClick={() => setDetailId(r.id)}>
                    Evidence
                  </Button>
                  {r.status === 'OPEN' && canManage ? (
                    <>
                      <Button
                        size="small"
                        type="primary"
                        loading={promoteMut.isPending}
                        onClick={() => confirmPromote(r.id, r.case_type)}
                      >
                        {r.case_type === 'METER' ? 'Initiate meter update' : 'Open equipment'}
                      </Button>
                      <Button size="small" onClick={() => setDismissOpen(r.id)}>
                        Dismiss
                      </Button>
                    </>
                  ) : null}
                </Space>
              ),
            },
          ]}
        />
      </Space>

      <Modal
        title="Reconciliation evidence"
        open={Boolean(detailId)}
        onCancel={() => setDetailId(null)}
        footer={null}
        width={720}
      >
        {detailQ.isLoading ? (
          <Card loading />
        ) : detailQ.data ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="Type">{detailQ.data.case_type}</Descriptions.Item>
              <Descriptions.Item label="Severity">{detailQ.data.severity}</Descriptions.Item>
              <Descriptions.Item label="Status">{detailQ.data.status}</Descriptions.Item>
              <Descriptions.Item label="Detail">{detailQ.data.detail || '—'}</Descriptions.Item>
              <Descriptions.Item label="Recommended">
                {detailQ.data.recommended_action || '—'}
              </Descriptions.Item>
              {detailQ.data.dismissal_reason ? (
                <Descriptions.Item label="Dismissal">{detailQ.data.dismissal_reason}</Descriptions.Item>
              ) : null}
            </Descriptions>
            <Card size="small" title="evidence_json">
              <pre style={{ margin: 0, maxHeight: 280, overflow: 'auto', fontSize: 12 }}>
                {JSON.stringify(detailQ.data.evidence_json ?? {}, null, 2)}
              </pre>
            </Card>
          </Space>
        ) : (
          <Alert type="error" message="Case not found" />
        )}
      </Modal>

      <Modal
        title="Dismiss reconciliation case"
        open={Boolean(dismissOpen)}
        onCancel={() => {
          setDismissOpen(null);
          setDismissReason('');
        }}
        okText="Dismiss"
        confirmLoading={dismissMut.isPending}
        onOk={() => {
          if (!dismissOpen || !dismissReason.trim()) {
            message.error('Dismissal reason is required');
            return Promise.reject();
          }
          return dismissMut.mutateAsync({ id: dismissOpen, reason: dismissReason.trim() });
        }}
      >
        <Form form={dismissForm} layout="vertical">
          <Form.Item label="Reason" required>
            <Input.TextArea
              rows={3}
              value={dismissReason}
              onChange={(e) => setDismissReason(e.target.value)}
              placeholder="Why is this not actionable now?"
            />
          </Form.Item>
          <Text type="secondary">Evidence is retained. This does not change meters or ops status.</Text>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};
